import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Phone, Sparkles, MapPin, AlertTriangle, CheckCircle2, MessageSquare, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { clientsApi, staffApi } from '../lib/api';
import { deliveryStages, contactStatuses } from '../constants';
import { stageClass, formatDate, getReadinessDetails } from '../lib/utils';
import { toast } from 'sonner';
import ImportFromVYDialog from '../components/ImportFromVYDialog';
import PaginationControls from '../components/PaginationControls';
import { useSite } from '../contexts/SiteContext';

const empty = { name: '', phone: '', email: '', vehicle: '', rego: '', vin: '', delivery_date: '', stage: 'Scheduled', salesperson: '', secondary_salesperson: '', delivery_consultant: '', handover_specialist: '', site_location: 'BYD Caroline Springs', notes: '', location: '', address: '' };

const contactBadge = {
  'Not Contacted': 'bg-rose-100 text-rose-700',
  'Contacted': 'bg-emerald-100 text-emerald-700',
  'Awaiting Reply': 'bg-amber-100 text-amber-700',
  'Booked': 'bg-blue-100 text-blue-700',
};

export default function Clients() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { activeSite, setActiveSite, siteLocations, isSiteMatch } = useSite();
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('All');
  const [contactFilter, setContactFilter] = useState('All');
  const [agentFilter, setAgentFilter] = useState('All');
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const reload = useCallback(async () => {
    setLoading(true);
    const apiParams = {};
    if (activeSite && activeSite !== 'All Sites') {
      apiParams.site_location = activeSite;
    }
    const list = await clientsApi.list(apiParams);
    setClients(list);
    setLoading(false);
  }, [activeSite]);

  useEffect(() => {
    reload();
    let cancelled = false;
    setAgents([]);
    staffApi.list(activeSite).then((u) => { if (!cancelled) setAgents(u); }).catch(() => { if (!cancelled) toast.error('Unable to load site staff'); });
    return () => { cancelled = true; };
  }, [reload, activeSite]);

  useEffect(() => {
    if (params.get('new') === '1') { setOpen(true); params.delete('new'); setParams(params); }
  }, [params, setParams]);

  const filterParam = params.get('filter');

  const readyCount = useMemo(() => {
    return clients.filter((c) => getReadinessDetails(c).isReady && c.stage !== 'Delivered').length;
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const todayIso = new Date().toISOString().slice(0, 10);
    return clients.filter((c) => {
      const matches = !q || c.name.toLowerCase().includes(q) || c.vehicle.toLowerCase().includes(q) || (c.rego || '').toLowerCase().includes(q) || (c.phone || '').includes(q) || (c.vin || '').toLowerCase().includes(q);
      const stageOk = stageFilter === 'All' || c.stage === stageFilter;
      const contactOk = contactFilter === 'All' || c.contact_status === contactFilter;
      const agentOk = agentFilter === 'All' ||
        (agentFilter === 'Unassigned' ? !c.assigned_agent_id : c.assigned_agent_id === agentFilter);
      const siteOk = isSiteMatch(c.site_location, activeSite);

      let exceptionOk = true;
      if (filterParam === 'ready_for_delivery') {
        exceptionOk = getReadinessDetails(c).isReady && c.stage !== 'Delivered';
      } else if (filterParam === 'overdue') {
        exceptionOk = (c.arrived && c.stage !== 'Delivered') || (c.delivery_date && c.delivery_date < todayIso && c.stage !== 'Delivered');
      } else if (filterParam === 'trade_in') {
        exceptionOk = ['Expiring', 'Expired', 'At risk'].includes(c.trade_in_status) || c.trade_in_flag;
      } else if (filterParam === 'unallocated') {
        exceptionOk = (!c.vin || !c.rego) && c.stage !== 'Delivered';
      } else if (filterParam === 'docs') {
        exceptionOk = ['Requested', 'Partial'].includes(c.document_completeness);
      }

      return matches && stageOk && contactOk && agentOk && siteOk && exceptionOk;
    });
  }, [clients, search, stageFilter, contactFilter, agentFilter, filterParam, isSiteMatch, activeSite]);

  const agentMap = useMemo(() => {
    const m = {};
    agents.forEach((a) => { m[a.id] = a.name; });
    return m;
  }, [agents]);

  useEffect(() => { setPage(1); }, [search, stageFilter, contactFilter, agentFilter, activeSite]);

  const pagedClients = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page],
  );

  const handleSave = async () => {
    if (!form.name || !form.phone || !form.vehicle) { toast.error('Name, phone and vehicle are required'); return; }
    setBusy(true);
    try {
      await clientsApi.create(form);
      toast.success(`${form.name} added to deliveries`);
      setOpen(false); setForm(empty);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-neutral-500 mt-1">{clients.length} customers in your delivery pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-1.5 border-neutral-300">
            <Sparkles className="h-4 w-4 text-[#E11B22]"/> Import from VY
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#E11B22] hover:bg-[#B81319] text-white"><Plus className="h-4 w-4 mr-1.5"/> Add client</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New delivery client</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <Field label="Full name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Mobile (E.164)"><Input placeholder="+61412345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Vehicle"><Input placeholder="BYD Atto 3" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} /></Field>
                <Field label="Dealership Site Location">
                  <Select value={form.site_location || (activeSite !== 'All Sites' ? activeSite : 'BYD Caroline Springs')} onValueChange={(v) => setForm({ ...form, site_location: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{siteLocations.filter((s) => s !== 'All Sites').map((site) => <SelectItem key={site} value={site}>{site}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Rego"><Input value={form.rego} onChange={(e) => setForm({ ...form, rego: e.target.value })} /></Field>
                <Field label="Street Address"><Input placeholder="123 Main St, Suburb VIC" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
                <Field label="Delivery date"><Input type="date" value={form.delivery_date || ''} onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} /></Field>
                <Field label="Stage">
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{deliveryStages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Primary Salesperson"><Input placeholder="Primary sales rep" value={form.salesperson} onChange={(e) => setForm({ ...form, salesperson: e.target.value })} /></Field>
                <Field label="Secondary Salesperson"><Input placeholder="Secondary sales rep (optional)" value={form.secondary_salesperson} onChange={(e) => setForm({ ...form, secondary_salesperson: e.target.value })} /></Field>
                <Field label="Delivery Follow-up Consultant"><Input placeholder="Consultant for follow-up" value={form.delivery_consultant} onChange={(e) => setForm({ ...form, delivery_consultant: e.target.value })} /></Field>
                <Field label="Handover Specialist" full><Input placeholder="Staff conducting physical handover" value={form.handover_specialist} onChange={(e) => setForm({ ...form, handover_specialist: e.target.value })} /></Field>
                <Field label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={busy} className="bg-[#E11B22] hover:bg-[#B81319] text-white">Save client</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <ImportFromVYDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />

      {/* Site Filter Pills */}
      <div className="flex items-center gap-2.5 flex-wrap bg-white p-3 rounded-xl border border-neutral-200 shadow-sm">
        <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider flex items-center gap-1.5 px-1">
          <MapPin className="h-4 w-4 text-[#E11B22]" /> Dealership Site:
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {siteLocations.map((site) => {
            const isSelected = activeSite === site;
            return (
              <button
                key={site}
                type="button"
                onClick={() => setActiveSite(site)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2 cursor-pointer ${
                  isSelected
                    ? 'bg-[#E11B22] text-white shadow-sm font-semibold'
                    : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                }`}
              >
                <span>{site}</span>
                {isSelected && (
                  <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full font-mono text-white">
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input placeholder="Search by name, vehicle or rego…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>
        {filterParam && (
          <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300 gap-1 font-semibold py-1.5 px-2.5">
            Filter: {filterParam}
            <button onClick={() => { params.delete('filter'); setParams(params); }} className="hover:text-red-600 font-bold ml-1">×</button>
          </Badge>
        )}
        <Button
          variant={filterParam === 'ready_for_delivery' ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterParam === 'ready_for_delivery') {
              params.delete('filter');
            } else {
              params.set('filter', 'ready_for_delivery');
            }
            setParams(params);
          }}
          className={`gap-1.5 text-xs font-semibold ${filterParam === 'ready_for_delivery' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-white border-neutral-200 text-neutral-700'}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
          Ready For Delivery
          <Badge className={`ml-1 text-[10px] px-1 py-0 ${filterParam === 'ready_for_delivery' ? 'bg-emerald-800 text-white' : 'bg-neutral-100 text-neutral-700'}`}>
            {readyCount}
          </Badge>
        </Button>
        <Select value={contactFilter} onValueChange={setContactFilter}>
          <SelectTrigger className="w-44 bg-white"><SelectValue placeholder="Contact status"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All contact</SelectItem>
            {contactStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-44 bg-white"><SelectValue placeholder="Agent"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All agents</SelectItem>
            <SelectItem value="Unassigned">Unassigned</SelectItem>
            {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={stageFilter} onValueChange={setStageFilter}>
        <TabsList className="bg-white border border-neutral-200">
          <TabsTrigger value="All">All</TabsTrigger>
          {deliveryStages.map((s) => <TabsTrigger key={s} value={s}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <Card className="border-neutral-200 overflow-hidden">
        <div className="hidden md:grid grid-cols-12 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold uppercase tracking-wide text-neutral-500">
          <div className="col-span-3">Customer</div>
          <div className="col-span-3">Vehicle &amp; location</div>
          <div className="col-span-2">Delivery</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2">Agent</div>
        </div>
        <div className="divide-y divide-neutral-100">
          {loading && <div className="px-6 py-8 text-center text-neutral-500 text-sm">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-6 py-12 text-center text-neutral-500 text-sm">
              No clients match your filters for {activeSite === 'All Sites' ? 'all sites' : activeSite}.
            </div>
          )}
          {pagedClients.map((c) => {
            const arrivedPending = c.arrived && c.stage !== 'Delivered';
            const readiness = getReadinessDetails(c);
            return (
              <div
                key={c.id}
                onClick={() => navigate(`/clients/${c.id}`)}
                className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-0 px-6 py-4 items-center hover:bg-neutral-50 cursor-pointer transition-colors ${
                  readiness.isReady
                    ? 'bg-emerald-50/20 hover:bg-emerald-50/40'
                    : arrivedPending
                    ? 'bg-amber-50/40 hover:bg-amber-50/60'
                    : ''
                }`}
              >
                <div className="md:col-span-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-sm">{c.name}</p>
                    {readiness.isReady && (
                      <Badge className="bg-emerald-600 text-white font-bold text-[9px] px-1.5 py-0 h-4 border-0">
                        Ready
                      </Badge>
                    )}
                    {arrivedPending && <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1"/>Arrived</Badge>}
                  </div>
                  <div className="flex gap-3 text-xs text-neutral-500 mt-0.5">
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3"/>{c.phone}</span>
                  </div>
                </div>
                <div className="md:col-span-3 text-sm">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className="font-medium">{c.vehicle}</p>
                    {c.site_location && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-neutral-100 text-neutral-700 border-neutral-300 font-medium">
                        {c.site_location}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-neutral-500 flex items-center gap-1.5 mt-0.5">
                    {c.location && <><MapPin className="h-3 w-3"/> {c.location}</>}
                    {c.rego && <span>· Rego: {c.rego}</span>}
                  </p>
                </div>
                <div className="md:col-span-2 text-sm">{formatDate(c.delivery_date)}</div>
                <div className="md:col-span-2 flex flex-col gap-1 items-start">
                  {readiness.isReady ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 border font-bold text-[10px] flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                      Ready For Delivery
                    </Badge>
                  ) : (
                    <Badge className={`${stageClass(c.stage)} border-0 w-fit`}>{c.stage}</Badge>
                  )}
                  {c.contact_status && (
                    <Badge className={`${contactBadge[c.contact_status] || 'bg-neutral-100'} border-0 w-fit text-[10px]`}>
                      {c.contact_status === 'Contacted' ? <CheckCircle2 className="h-3 w-3 mr-1"/> : <MessageSquare className="h-3 w-3 mr-1"/>}
                      {c.contact_status}
                    </Badge>
                  )}
                  {/* Mini readiness indicator */}
                  <div className="flex items-center gap-0.5 text-[9px] font-mono mt-0.5">
                    <span className={`px-1 rounded ${readiness.payment ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-neutral-100 text-neutral-400'}`} title="Payment">P</span>
                    <span className={`px-1 rounded ${readiness.tradeInDocs ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-neutral-100 text-neutral-400'}`} title="Trade-in Docs">T</span>
                    <span className={`px-1 rounded ${readiness.pdi ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-neutral-100 text-neutral-400'}`} title="PDI">I</span>
                    <span className={`px-1 rounded ${readiness.registrationDocs ? 'bg-emerald-100 text-emerald-800 font-bold' : 'bg-neutral-100 text-neutral-400'}`} title="Registration Docs">R</span>
                  </div>
                  {c.trade_in_attached && c.trade_in_valid_until && (
                    (() => {
                      const daysLeft = Math.ceil((new Date(`${c.trade_in_valid_until}T23:59:59`) - new Date()) / 86400000);
                      if (daysLeft <= 0) return <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">Trade-in expired</Badge>;
                      return <Badge className={`${daysLeft <= 10 ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 text-neutral-600'} border-0 text-[10px]`}>Trade-in: {daysLeft}d</Badge>;
                    })()
                  )}
                </div>
                <div className="md:col-span-2 text-sm text-neutral-600">
                  {c.assigned_agent_id ? agentMap[c.assigned_agent_id] || 'Agent' : <span className="text-neutral-400 italic">Unassigned</span>}
                </div>
              </div>
            );
          })}
        </div>
        <PaginationControls
          page={page}
          pageCount={Math.ceil(filtered.length / pageSize)}
          total={filtered.length}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      </Card>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs font-semibold text-neutral-700 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
